/**
 * A number box in a form that records or corrects a fact (F-86, A-#491 4, 30 October 2026).
 *
 * A type="number" input reports its value as '' both when it is empty and when it holds text the browser cannot read
 * ("1e", "--5"), so a form that turned '' into null erased a recorded value in a correction, silently: the visit's
 * minutes, a logged communication's minutes; and recorded a court order's visit minimum as none. The rule:
 *   - text the box cannot read (the input's validity.badInput) is INVALID: nothing is sent and Save is held;
 *   - a box that says what is stored is KEEP: the stored value is sent as it is;
 *   - an empty box over a stored value is CLEAR: sent as null only when the person has said to remove it;
 *   - anything else is a whole number in range (SET) or INVALID.
 */

export type NumberEdit =
  | { kind: 'keep' }
  | { kind: 'set'; value: number }
  | { kind: 'clear' }
  | { kind: 'invalid' };

export interface NumberRange { min: number; max: number }

export function numberEdit(text: string, badInput: boolean, stored: number | null, range: NumberRange): NumberEdit {
  if (badInput) return { kind: 'invalid' };
  const t = text.trim();
  if (t === '') return stored === null ? { kind: 'keep' } : { kind: 'clear' };
  if (!/^\d+$/.test(t)) return { kind: 'invalid' };
  const n = Number(t);
  if (n < range.min || n > range.max) return { kind: 'invalid' };
  return n === stored ? { kind: 'keep' } : { kind: 'set', value: n };
}

/** What the request carries: the number, null (nothing recorded, or a confirmed removal), or undefined: send nothing. */
export function numberToSend(e: NumberEdit, stored: number | null, removeConfirmed: boolean): number | null | undefined {
  switch (e.kind) {
    case 'keep': return stored;
    case 'set': return e.value;
    case 'clear': return removeConfirmed ? null : undefined;
    case 'invalid': return undefined;
  }
}

export function numberWords(range: NumberRange): string {
  return `a whole number from ${range.min} to ${range.max}`;
}
